<script setup lang="ts">
useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/legal/privacy-policy`,
  }],
})

useSeoMeta({
  title: 'Privacy Policy - SpotHopper',
  ogTitle: 'Privacy Policy - SpotHopper',
  ogImage: `${S3_ASSETS_URL}/meta-default.jpg`,
})
</script>

<template>
  <LayoutLegal>
    <h1 class="mb-5 text-[2.5rem] font-medium">
      Privacy Policy
    </h1>

    <time datetime="05-01-2024">
      Last revised: January 5, 2024
    </time>

    <p>
      This Privacy Policy governs the manner in which SpotHopper Inc. collects, uses, maintains and discloses information collected from users (each, a "User") of the <a href="https://www.spothopperapp.com/">https://www.spothopperapp.com/</a> website ("Site"). This privacy policy applies to the Site and all products and services offered by SpotHopper Inc.
    </p>

    <ol class="marker:text-[var(--tw-prose-bold)] [&>li]:marker:font-bold [&>li_p]:mt-1">
      <li>
        <strong>
          Personal identification information
        </strong>

        <p class="-ml-8">
          We may collect personal identification information from Users in a variety of ways, including, but not limited to, when Users visit our site, register on the site, fill out a form, and in connection with other activities, services, features or resources we make available on our Site. Users may be asked for name, email address and cellular number. Users may, however, visit our Site anonymously. We will collect personal identification information from Users only if they voluntarily submit such information to us. Users can always refuse to supply personal identification information, except that it may prevent them from engaging in certain Site related activities and obtaining services.
        </p>
      </li>

      <li>
        <strong>
          Non-personal identification information
        </strong>

        <p class="-ml-8">
          We may collect non-personal identification information about Users whenever they interact with our Site. Non-personal identification information may include the browser name, the type of computer and technical information about Users' means of connection to our Site, such as the operating system and the Internet service providers utilized and other similar information.
        </p>
      </li>

      <li>
        <strong>
          Web browser cookies
        </strong>

        <p class="-ml-8">
          Our Site may use "cookies" to enhance User experience. User's web browser places cookies on their hard drive for record-keeping purposes and sometimes to track information about them. User may choose to set their web browser to refuse cookies, or to alert you when cookies are being sent. If they do so, note that some parts of the Site may not function properly.
        </p>
      </li>

      <li>
        <strong>
          How we use collected information
        </strong>

        <p class="-ml-8">
          SpotHopper Inc. may collect and use Users’ personal information for the following purposes:
        </p>

        <ul class="-ml-4 list-none p-0 [&_li]:mt-0 [&_li_ul]:mt-0">
          <li>
            <strong class="text-zinc-600">
              To improve customer service
            </strong>

            <ul>
              <li>
                Information you provide helps us respond to your customer service requests and support needs more efficiently.
              </li>
            </ul>
          </li>

          <li>
            <strong class="text-zinc-600">
              To personalize user experience
            </strong>

            <ul>
              <li>
                We may use information in the aggregate to understand how our Users as a group use the services and resources provided on our Site.
              </li>
            </ul>
          </li>

          <li>
            <strong class="text-zinc-600">
              To improve our Site
            </strong>

            <ul>
              <li>
                We may use feedback you provide to improve our products and services.
              </li>
            </ul>
          </li>

          <li>
            <strong class="text-zinc-600">
              To send periodic emails/text messages
            </strong>

            <ul>
              <li>
                We may use the email address to respond to their inquiries, questions, and/or other requests. If User decides to opt-in to our mailing list, they will receive emails and/or text messages that may include company news, updates, related product or service information, etc. If at any time the User would like to unsubscribe from receiving future emails, we include detailed unsubscribe instructions at the bottom of each email. If User would like to unsubscribe from future text messages other than those related directly to a service request, the User may reply STOP to any SpotHopper message containing marketing or solicitation for new services. Further texts may be received to confirm the request and during the time period required for us to remove you from the distribution list.
              </li>

              <li>
                Message Senders should maintain and conspicuously display a privacy policy that is easily accessed by the Consumer (e.g., through clearly labelled links) and that clearly describes how the Message Sender may collect, use, and share information from Consumers. No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
              </li>
            </ul>
          </li>
        </ul>
      </li>

      <li>
        <strong>
          How we protect your information
        </strong>

        <p class="-ml-8">
          We adopt appropriate data collection, storage and processing practices and security measures to protect against unauthorized access, alteration, disclosure or destruction of your personal information, username, password, transaction information and data stored on our Site.
        </p>

        <p class="-ml-8">
          Sensitive and private data exchange between the Site and its Users happens over a SSL secured communication channel and is encrypted and protected with digital signatures. Our Site is also in compliance with PCI vulnerability standards in order to create as secure of an environment as possible for Users.
        </p>
      </li>

      <li>
        <strong>
          Sharing your personal information
        </strong>

        <p class="-ml-8">
          We do not sell, trade, or rent Users personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our business partners, trusted affiliates and advertisers for the purposes outlined above.We may use third party service providers to help us operate our business and the Site or administer activities on our behalf, such as sending out newsletters or surveys. We may share your information with these third parties for those limited purposes provided that you have given us your permission.
        </p>
      </li>

      <li>
        <strong>
          Requesting Personal Data Removal or Retrieval
        </strong>

        <p class="-ml-8">
          To request personal data removal or retrieval, please contact us via email at <a href="mailto:support@spothopperapp.com">support@spothopperapp.com</a>.
        </p>
      </li>

      <li>
        <strong>
          Third party websites
        </strong>

        <p class="-ml-8">
          Users may find advertising or other content on our Site that link to the sites and services of our partners, suppliers, advertisers, sponsors, licensors and other third parties. We do not control the content or links that appear on these sites and are not responsible for the practices employed by websites linked to or from our Site. In addition, these sites or services, including their content and links, may be constantly changing. These sites and services may have their own privacy policies and customer service policies. Browsing and interaction on any other website, including websites which have a link to our Site, is subject to that website's own terms and policies.
        </p>
      </li>

      <li>
        <strong>
          Compliance with children's online privacy protection act
        </strong>

        <p class="-ml-8">
          Protecting the privacy of the very young is especially important. For that reason, we never collect or maintain information at our Site from those we actually know are under 13, and no part of our website is structured to attract anyone under 13.
        </p>
      </li>

      <li>
        <strong>
          Changes to this privacy policy
        </strong>

        <p class="-ml-8">
          SpotHopper Inc. has the discretion to update this privacy policy at any time. When we do, we will revise the updated date at the bottom of this page. We encourage Users to frequently check this page for any changes to stay informed about how we are helping to protect the personal information we collect. You acknowledge and agree that it is your responsibility to review this privacy policy periodically and become aware of modifications.
        </p>
      </li>

      <li>
        <strong>
          Your acceptance of these terms
        </strong>

        <p class="-ml-8">
          By using this Site, you signify your acceptance of this policy. If you do not agree to this policy, please do not use our Site. Your continued use of the Site following the posting of changes to this policy will be deemed your acceptance of those changes.
        </p>
      </li>

      <li>
        <strong>
          Contacting us
        </strong>

        <p class="-ml-8">
          If you have any questions about this Privacy Policy, the practices of this site, or your dealings with this site, please contact us at:

          <ul class="list-none pl-0 [&>li]:pl-0">
            <li>
              SpotHopper Inc.
            </li>

            <li>
              <a href="https://www.spothopperapp.com/">https://www.spothopperapp.com/</a>
            </li>

            <li>
              <a href="mailto:privacy@spothopperapp.com">privacy@spothopperapp.com</a>
            </li>
          </ul>
        </p>
      </li>
    </ol>
  </LayoutLegal>
</template>
